import type {FocusEventHandler} from 'react';
import React, {useCallback, useMemo, useRef} from 'react';
import type {SxProps, Theme} from '@mui/material';
import {Box, IconButton} from '@mui/material';
import type {
  Cardinality,
  FieldRequiredSimple,
  PartName,
} from 'schemaDefinition/types';
import {noOp} from 'services/utils';
import {hasMaxItems} from 'schema/form/functions/hasMaxItems';
import type {BaseFieldProps, ContentLabelWithLayout} from './types';
import {useStaticAlertDialogAsPromise} from '../alertdialog';
import {DndContainer} from '../dnd/DndContainer';
import {Icon} from '../icons';
import {FlexBox, Layout} from '../layout';
import {Spacer} from '../spacer';
import {SuggestionsTooltip} from '../suggestionsPreview';
import {EmptyListPlaceholder} from './EmptyListPlaceholder';
import {FieldErrorMessage} from './FieldErrorMessage';
import {FieldLabel} from './FieldLabel';
import {FieldLayout} from './FieldLayout';
import {SingleValueListFieldActions} from './SingleValueListFieldActions';
import {useNextFocus} from './hooks';
import {useGetValueId} from './hooks/useGetValueId';

type SingleValueListFieldProps<T> = BaseFieldProps & {
  addLabel?: string;
  deleteAllStrings?: {
    label?: string;
    confirm: {
      title?: string;
      okTitle?: string;
      cancelTitle?: string;
    };
  };
  /** Will not show label when contentLabels are provided */
  contentLabels?: ContentLabelWithLayout[];
  /** Color for item container, including both move/delete buttons and fields */
  itemBackgroundColor?: string;
  /** Padding for item container */
  itemPadding?: string | number;
  /**
   * Number of hidden items to add as padding, for this list
   * to align with compared list when comparing two lists
   */
  valuePaddingSize?: number;
  name: PartName;
  required?: FieldRequiredSimple;
  cardinality?: Cardinality;
  values: T[] | undefined;
  getValueKey: (index: number, value: T) => string;
  renderValue: (
    index: number,
    value: T,
    id: string,
    readonly: boolean,
    showWhenReadonlyAndEmpty: boolean,
  ) => React.ReactNode;
  showStructureOperationInTopOfList?: boolean;
  renderCustomActions?: () => React.ReactNode;
  onDeleteValue?: (index: number) => T[];
  onAppendValue?: (prepend?: boolean) => T[];
  onDeleteAll?: () => T[];
  onMoveValue?: (oldIndex: number, newIndex: number) => T[];
  onBlur?: FocusEventHandler<HTMLDivElement> | undefined;
};

const buttonSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
};

const dragButtonSx: SxProps<Theme> = {
  ...buttonSx,
  cursor: 'grab',
};

const dragContainerSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
  ':focus-visible': {
    outline: 'none',
  },
};

const placeholderLayoutSx = {maxHeight: '1px'} as const;

export function SingleValueListField<T>({
  label,
  contentLabels,
  addLabel,
  deleteAllStrings,
  itemBackgroundColor,
  itemPadding = 0.5,
  valuePaddingSize,
  name,
  required = false,
  error,
  errorMessage,
  cardinality,
  values = [],
  readonly = false,
  showWhenReadonlyAndEmpty = false,
  getValueKey,
  renderValue,
  showStructureOperationInTopOfList,
  renderCustomActions,
  onAppendValue,
  onDeleteValue,
  onDeleteAll,
  onMoveValue,
  onBlur,
  'data-cy': dataCy,
  suggestions,
  layout,
}: SingleValueListFieldProps<T>) {
  const getElementId = useCallback(
    (subKey: string, idx: number) => {
      return getValueKey(idx, values[idx]);
    },
    [getValueKey, values],
  );

  const {scopeRef, setNextFocusId} = useNextFocus();

  const ignoreNextAddBlurRef = useRef(false);

  const {
    pleaseConfirm: confirmDeleteAll,
    AlertDialog: DeleteAllDialog,
    isOpen: isDeleteAllOpen,
    isClosing: isDeleteAllClosing,
  } = useStaticAlertDialogAsPromise({
    title: deleteAllStrings?.confirm?.title ?? 'Missing: Slett alle?',
    okTitle: deleteAllStrings?.confirm?.okTitle ?? 'Missing: Slett',
    cancelTitle: deleteAllStrings?.confirm?.cancelTitle ?? 'Missing: Avbryt',
  });

  const handleAppendValue = useCallback(
    (prepend?: boolean) => {
      if (onAppendValue) {
        onAppendValue(prepend);
        // Ignore blur of add button when setting focus on input field in list
        // to avoid indicating error/warning while adding first element to list
        ignoreNextAddBlurRef.current = true;
        setNextFocusId(getElementId('innerField', values.length));
      }
    },
    [getElementId, onAppendValue, setNextFocusId, values.length],
  );

  const handleDeleteAll = useCallback(() => {
    if (onDeleteAll) {
      confirmDeleteAll().then(confirmed => {
        if (confirmed) {
          onDeleteAll();
        }
      });
    }
  }, [confirmDeleteAll, onDeleteAll]);

  const handleAddBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!ignoreNextAddBlurRef.current) {
        onBlur && onBlur(e);
      }

      ignoreNextAddBlurRef.current = false;
    },
    [onBlur],
  );

  const handleDeleteValue = useCallback(
    (index: number) => {
      if (onDeleteValue) {
        onDeleteValue(index);
        setNextFocusId(getElementId('addDeleteBtn', index));
      }
    },
    [getElementId, onDeleteValue, setNextFocusId],
  );

  const canMove = !readonly && onMoveValue !== undefined && values.length > 1;
  const canDelete =
    !readonly &&
    onDeleteValue !== undefined &&
    (required !== true || values.length > 1);
  const canAdd =
    !readonly && !hasMaxItems(values, cardinality) && !!onAppendValue;
  const canDeleteAll =
    !readonly && onDeleteAll !== undefined && values.length > 1;

  const spacedContentLabelsWithLayout = useMemo(() => {
    return contentLabels
      ? contentLabels.reduce<(ContentLabelWithLayout | null)[]>(
          (acc, cl, idx) => {
            idx === 0 ? acc.push(cl) : acc.push(null, cl);
            return acc;
          },
          [],
        )
      : undefined;
  }, [contentLabels]);

  const sxItem = useMemo(
    () =>
      ({
        background: itemBackgroundColor,
        padding: itemPadding,
        borderRadius: itemPadding,
      }) as const,
    [itemBackgroundColor, itemPadding],
  );

  const idGetter = useGetValueId(values);

  const valuesWithPadding = useMemo(() => {
    const paddingItems =
      (valuePaddingSize ?? 0) > 0
        ? new Array(valuePaddingSize).fill(onAppendValue?.() ?? null)
        : [];
    return [...values, ...paddingItems].map((v, idx) => ({
      value: v,
      // These ids will be passed to DND. Id cannot be 0, so convert to strings.
      id: idGetter(idx),
    }));
  }, [idGetter, onAppendValue, valuePaddingSize, values]);

  // Only indicate error on add button if no values
  const addError = values.length > 0 ? false : error;

  return (
    <div ref={scopeRef} style={layout} data-cy={dataCy ?? `field-${name}`}>
      {/* Label */}
      {spacedContentLabelsWithLayout === undefined ? (
        <Layout horizontal>
          <Layout horizontal adjustCenter>
            {label ? (
              <FieldLabel label={label} required={required} error={error} />
            ) : null}
            <Spacer width={2} />
            <SuggestionsTooltip suggestions={suggestions} />
          </Layout>
          <FieldErrorMessage error={error} errorMessage={errorMessage} />
        </Layout>
      ) : valuesWithPadding && valuesWithPadding.length > 0 ? (
        // Content Labels
        <Layout horizontal px={itemPadding}>
          {canMove ? (
            // Placeholder for the space taken by the drag handle, if any
            <Layout hidden sx={placeholderLayoutSx}>
              <IconButton size="small" onClick={noOp}>
                <Icon icon={'Drag'} />
              </IconButton>
            </Layout>
          ) : null}
          <Layout
            horizontal
            marginLeft={canMove ? 1 : 0}
            marginRight={canDelete ? 1 : 0}
            flex={1}>
            {spacedContentLabelsWithLayout.map((l, idx) =>
              l === null ? (
                <Spacer key={idx} size={2} />
              ) : (
                <Layout horizontal {...l.layout} adjustLeft key={idx}>
                  {l.label ? (
                    <FieldLabel
                      key={l.label}
                      label={l.label}
                      required={l.required}
                      error={l.error}
                    />
                  ) : null}
                </Layout>
              ),
            )}
          </Layout>
          {canDelete ? (
            // Placeholder for space taken by delete button, if any
            <Layout hidden sx={placeholderLayoutSx}>
              <IconButton size="small" onClick={noOp}>
                <Icon icon={'Delete'} />
              </IconButton>
            </Layout>
          ) : null}
        </Layout>
      ) : null}
      {readonly ? (
        <FieldLayout tight>
          {valuesWithPadding && valuesWithPadding.length > 0 ? (
            valuesWithPadding.map(({value}, idx) => {
              const itemKey = getElementId('container', idx); // getValueKey(value, idx);
              return (
                <Layout key={itemKey} horizontal alignItems={'center'} pb={1}>
                  <Layout flex={1}>
                    {renderValue(
                      idx,
                      value,
                      getElementId('innerField', idx),
                      readonly,
                      showWhenReadonlyAndEmpty,
                    )}
                  </Layout>
                </Layout>
              );
            })
          ) : (
            <EmptyListPlaceholder />
          )}
        </FieldLayout>
      ) : (
        <Layout>
          {showStructureOperationInTopOfList && values?.length > 0 ? (
            <SingleValueListFieldActions
              handleAppendValue={handleAppendValue}
              handleDeleteAll={handleDeleteAll}
              canAdd={canAdd}
              // Don't allow delete all from top of list
              canDeleteAll={false}
              addLabel={addLabel}
              renderCustomActions={renderCustomActions}
              deleteAllStrings={deleteAllStrings}
              addError={addError}
              itemPadding={itemPadding}
              handleAddBlur={handleAddBlur}
              prependAdd={true}
            />
          ) : null}

          <DndContainer
            items={valuesWithPadding}
            onMove={(from, to) => onMoveValue?.(from, to)}
            renderer={(id, listeners) => {
              const itemIdx = valuesWithPadding.findIndex(v => v.id === id);
              const item = valuesWithPadding[itemIdx].value;

              return (
                <FlexBox horizontal alignItems={'center'} sx={sxItem}>
                  {canMove ? (
                    <Box tabIndex={-1} sx={dragContainerSx} {...listeners}>
                      <IconButton
                        color="primary"
                        sx={dragButtonSx}
                        size="small"
                        onClick={noOp}>
                        <Icon icon={'Drag'} />
                      </IconButton>
                    </Box>
                  ) : null}
                  <Layout
                    marginLeft={canMove ? 1 : 0}
                    marginRight={canDelete ? 1 : 0}
                    flex={1}>
                    {renderValue(
                      itemIdx,
                      item,
                      getElementId('innerField', itemIdx),
                      readonly,
                      showWhenReadonlyAndEmpty,
                    )}
                  </Layout>
                  {canDelete ? (
                    <IconButton
                      data-cy={'button-delete'}
                      aria-label={'Delete item button'}
                      color="primary"
                      size="small"
                      sx={buttonSx}
                      onClick={() => handleDeleteValue(itemIdx)}>
                      <Icon icon={'Delete'} />
                    </IconButton>
                  ) : null}
                </FlexBox>
              );
            }}
          />

          <SingleValueListFieldActions
            handleAppendValue={handleAppendValue}
            handleDeleteAll={handleDeleteAll}
            canAdd={canAdd}
            canDeleteAll={canDeleteAll}
            addLabel={addLabel}
            renderCustomActions={renderCustomActions}
            deleteAllStrings={deleteAllStrings}
            addError={addError}
            itemPadding={itemPadding}
            handleAddBlur={handleAddBlur}
            prependAdd={false}
          />
        </Layout>
      )}
      <DeleteAllDialog
        isOpen={isDeleteAllOpen}
        isClosing={isDeleteAllClosing}
      />
    </div>
  );
}
